/* eslint-disable no-param-reassign */
import axios from 'axios';
import urlData from '../config';

/**
 * GETs use the WebView fetch when CapacitorHttp has replaced window.fetch.
 * The native HTTP patch fixes login POSTs on the bundled shell, but a later
 * native GET can hang, which leaves Step 1 on the empty "FITNESS Complete!" slide.
 *
 * The live shell (server.url = dashboard) is a different case: patched XHR
 * never returns, while the same login works in Chrome. Those writes use the
 * unpatched fetch, same path as the browser.
 */
function isLivePortalWebView() {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return false;
    if (window.androidBridge || window.CapacitorHttpAndroidInterface) return true;
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.bridge) {
        return true;
    }
    return /;\s*wv\)/i.test(window.navigator.userAgent || '');
}

async function readBody(res) {
    const text = await res.text();
    if (!text) return text;
    try {
        return JSON.parse(text);
    } catch (error) {
        return text;
    }
}

async function requestGet(fullUrl, headers) {
    const doFetch = window.CapacitorWebFetch || window.fetch;
    const res = await doFetch(fullUrl, {
        method: 'GET',
        headers,
        signal:
            typeof AbortSignal !== 'undefined' && AbortSignal.timeout
                ? AbortSignal.timeout(20000)
                : undefined,
    });
    const body = await readBody(res);
    if (!res.ok) {
        const err = new Error('request failed');
        err.response = { status: res.status, data: body };
        throw err;
    }
    return body;
}

async function requestWrite(fullUrl, method, headers, data) {
    const doFetch = window.CapacitorWebFetch || window.fetch;
    const reqHeaders = { ...headers };
    if (!reqHeaders['Content-Type'] && !reqHeaders['content-type']) {
        reqHeaders['Content-Type'] = 'application/json';
    }
    const res = await doFetch(fullUrl, {
        method,
        headers: reqHeaders,
        body: data == null ? undefined : JSON.stringify(data),
        signal:
            typeof AbortSignal !== 'undefined' && AbortSignal.timeout
                ? AbortSignal.timeout(20000)
                : undefined,
    });
    const body = await readBody(res);
    if (!res.ok) {
        const err = new Error('request failed');
        err.response = { status: res.status, data: body };
        throw err;
    }
    return body;
}

const request = async (params) => {
    const { url, method = 'get', data = {}, headers = {}, token = '' } = params;

    try {
        // Resolve each call so LAN/phone rewrite in config.js always applies.
        const base = String(urlData.SERVER_URL || '').replace(/\/?$/, '/');
        const fullUrl = `${base}${url}`;

        if (token) headers.Authorization = `Bearer ${token}`;

        const verb = String(method || 'get').toLowerCase();
        if (verb === 'get') {
            const body = await requestGet(fullUrl, headers);
            return { error: '', data: body, status: 200 };
        }

        if (isLivePortalWebView() && !(data instanceof FormData)) {
            const body = await requestWrite(fullUrl, verb, headers, data);
            return { error: '', data: body, status: 200 };
        }

        const response = await axios({
            method,
            headers,
            url: fullUrl,
            data,
        });

        return { error: '', data: response.data, status: '' };
    } catch (err) {
        return { error: err, data: '', status: err.response?.status };
    }
};

export default request;
